import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../globals';
import { FormGroup, LabeledCheckbox } from '../../../generic/Forms';
import { UNSET_POLICY_OPTION } from '../../../shared/policies/hooks/usePolicies';
import Section from '../../../shared/settings/SettingsPageSection';
import Dropdown from '../../shared/SettingsPageDropdown';
import useOrganizationSettings from '../../shared/useOrganizationSettings';
import type { SBOMSettings } from '../../../../../shared/settings/project/sbom';

import PropagateSBOMModal from './PropagateSBOMModal';
import type { IssueSettingsProps } from './types';

/**
 * SettingsOrganizationSBOM is the settings page for
 * managing the SBOM policy settings for projects.
 */
const SettingsOrganizationSBOM: VoidFunctionComponent<IssueSettingsProps> = ({ policies }) => {
  const [isPropagateModalOpen, setIsPropagateModalOpen] = useState<boolean>(false);
  const { settings, isLoading, onUpdateSettings, onSaveSettings, isSaveButtonDisabled } =
    useOrganizationSettings<SBOMSettings>({
      endpoint: `/api/organizations/${window.user?.organizationId as number}/settings/projects/sbom`,
      settingsSection: 'project SBOM',
    });
  const selectedPolicy =
    policies.find((policy) => policy.value === settings?.defaultSBOMPolicyId) || UNSET_POLICY_OPTION;
  return (
    <>
      <Section>
        <Section.Left heading="SBOMs">
          <Section.Paragraph>Default configuration for imported SBOM projects.</Section.Paragraph>
          <Section.Buttons>
            <Section.PropagateButton onClick={() => setIsPropagateModalOpen(true)} isDisabled={!isSaveButtonDisabled} />
            <Section.SaveButton onClick={onSaveSettings} isLoading={isLoading.save} isDisabled={isSaveButtonDisabled} />
          </Section.Buttons>
        </Section.Left>
        <Section.Right>
          <Section.Heading>Default SBOM settings</Section.Heading>
          <LabeledCheckbox
            label="Enable SBOM analysis"
            isChecked={settings?.projectSBOMAnalysisEnabled || false}
            onChange={(_event, isChecked: boolean) => onUpdateSettings({ projectSBOMAnalysisEnabled: isChecked })}
          />
          <Section.Indented isDisabled={!settings?.projectSBOMAnalysisEnabled}>
            <FormGroup spacing="xs">
              <Dropdown
                id="settings-project-sbom-policy"
                label="Default SBOM policy"
                options={policies}
                value={selectedPolicy}
                onChange={(option) => {
                  if (option) {
                    onUpdateSettings({ defaultSBOMPolicyId: option.value as number });
                  }
                }}
                isDisabled={!settings?.projectSBOMAnalysisEnabled}
              />
              <Section.Paragraph>This policy will be applied by default to all new SBOM projects.</Section.Paragraph>
            </FormGroup>
          </Section.Indented>
        </Section.Right>
      </Section>
      {settings && (
        <PropagateSBOMModal
          settings={settings}
          isOpen={isPropagateModalOpen}
          onHide={() => setIsPropagateModalOpen(false)}
          policyName={selectedPolicy.label}
        />
      )}
    </>
  );
};

export default SettingsOrganizationSBOM;
